import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Apiconnect from "../services/Apiconnect.js";

const PackageAdd = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    distribution_price: '',
    pack_validity: '',
    info: ''
  });

  // Determine if this is an edit or add operation
  const isEditMode = !!id;

  useEffect(() => {
    // If ID is provided, fetch the package data for editing
    if (isEditMode) {
      fetchPackageDetails();
    }
  }, [id]);

  const fetchPackageDetails = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error("Authentication token not found. Please login again.");
        return;
      }

      const response = await Apiconnect.getData(`package/${id}`);
      
      if (response.data && response.data.status === 'success') {
        const packageData = response.data.data;
        setFormData({
          name: packageData.name || '',
          price: packageData.price || '',
          distribution_price: packageData.distribution_price || '',
          pack_validity: packageData.pack_validity || '',
          info: packageData.info || ''
        });
      } else {
        const errorMessage = response.data?.message || 'Failed to fetch package details';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error fetching package details:", error);
      toast.error('An error occurred while fetching package details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error("Authentication token not found. Please login again.");
        return;
      }

      let response;
      if (isEditMode) {
        // Update existing package
        response = await Apiconnect.postData(`package/${id}`, formData);
      } else {
        // Create new package
        response = await Apiconnect.postData('package', formData);
      }

      if (response.data && response.data.status === 'success') {
        toast.success(isEditMode ? 'Package updated successfully!' : 'Package added successfully!');
        // Navigate back to package list after a short delay
        setTimeout(() => {
          navigate('/admin/package');
        }, 1500);
      } else {
        const errorMessage = response.data?.message || 'Operation failed';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error('An error occurred while processing your request');
    } finally {
      setIsLoading(false);
    }
  };

  const styles = {
    container: {
      fontFamily: 'Arial, sans-serif',
    },
    card: {
      border: '1px solid #dee2e6',
      borderRadius: '8px',
      backgroundColor: 'white',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      marginLeft: '11px',
      marginRight: '11px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '15px 20px',
      borderBottom: '1px solid #dee2e6',
      backgroundColor: '#5867DD',
      borderTopLeftRadius: '5px',
      borderTopRightRadius: '5px',
    },
    addButton: {
      backgroundColor: '#D1D2D2',
      color: 'black',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '5px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '15px',
      width: '95px',
      height: '35px',
    },
    title: {
      margin: 0,
      fontSize: '20px',
      color: 'white'
    },
    form: {
      padding: '20px'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      marginBottom: '5px',
      fontWeight: 'bold'
    },
    requiredLabel: {
      display: 'block',
      marginBottom: '5px',
      fontWeight: 'bold'
    },
    input: {
      width: '100%',
      padding: '10px',
      border: '1px solid #ced4da',
      borderRadius: '4px',
      fontSize: '16px'
    },
    textarea: {
      width: '100%',
      padding: '10px',
      border: '1px solid #ced4da',
      borderRadius: '4px',
      fontSize: '16px',
      minHeight: '200px',
      resize: 'vertical'
    },
    submitButton: {
      backgroundColor: '#272441',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '4px',
      fontSize: '16px',
      cursor: 'pointer'
    },
    row: {
      display: 'flex',
      flexWrap: 'wrap',
      margin: '0 -10px'
    },
    col: {
      flex: '1 0 30%',
      padding: '0 10px',
      boxSizing: 'border-box'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h4 style={styles.title}>{isEditMode ? 'Edit Package' : 'Add Package'}</h4>
          <Link to="/admin/package" style={styles.addButton}>
            <FaArrowLeft className="me-2" /> Back
          </Link>
        </div>
        <form style={styles.form} onSubmit={handleSubmit}>
          <div style={styles.row}>
            <div style={styles.col}>
              <div style={styles.formGroup}>
                <label style={styles.requiredLabel}>
                  Name<span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter Package Name"
                  style={styles.input}
                  required
                />
              </div>
            </div>
            <div style={styles.col}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Price</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="Enter Price"
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.col}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Distribution Price</label>
                <input
                  type="number"
                  name="distribution_price"
                  value={formData.distribution_price}
                  onChange={handleChange}
                  placeholder="Enter Distribution Price"
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.col}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Pack Validity (Days)</label>
                <input
                  type="number"
                  name="pack_validity"
                  value={formData.pack_validity}
                  onChange={handleChange}
                  placeholder="Enter Validity in Days"
                  style={styles.input}
                />
              </div>
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Information</label>
            <textarea
              name="info"
              value={formData.info}
              onChange={handleChange}
              placeholder="Enter package information"
              style={styles.textarea}
            ></textarea>
          </div>
          <div>
            <button type="submit" style={styles.submitButton} disabled={isLoading}>
              {isLoading ? 'Processing...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
      <ToastContainer />
    </div>
  );
};

export default PackageAdd;